<template>
  <template v-if="variant">
    <h1 class="flex flex-col">
      {{ productName }}
      <span class="text-xl font-normal mobile:text-sm">
        {{ productDescription }}
      </span>
    </h1>
    <div class="mb-6 mt-1 inline-block">
      <!-- eslint-disable-next-line vue/html-self-closing -->
      <etrusted-product-review-list-widget-product-star-extension></etrusted-product-review-list-widget-product-star-extension>
    </div>
    <div>
      <div class="flex items-end gap-1">
        <KikPrice
          class="mr-1"
          size="xl"
          :regular="variant.attributes?.discount_sale_value && variant.price.strike && $n(variant.price.strike, 'currency')"
          :special="finalPrice && $n(finalPrice, 'currency')"
        />
        <div
          v-if="saleTag"
          class="h-[30px] bg-primary-700 p-1 text-xs font-bold leading-6 text-white"
        >
          {{ saleTag }}%
        </div>
        <NuxtImg
          v-if="isMyKikDiscount"
          :alt="$t('myKiK')"
          src="/img/mykik.svg"
          width="47"
        />
        <KikDrawer
          v-if="$flags.hasnt('shop.catalog.enabled')"
          title="Shipping costs"
        >
          <template #button>
            <p class="ml-1 text-sm leading-4 underline mobile:text-xs">
              {{ $t('incl. VAT., excl. Shipping costs') }}
            </p>
          </template>
          <template #content>
            <KikProperty
              v-for="method in shippingMethodsByCountry"
              :key="method.id"
              :name="method.displayName"
              :value="$n(method.amount, 'currency')"
              class="mt-2 justify-between border-b border-neutral-100 pb-2 first-of-type:mt-4"
            >
              <template #name="{ title }">
                <p class="text-sm font-light">
                  {{ title }}
                  <template v-if="method.thresholdToActivate">
                    {{ $t('ShippingInformation2Threshold', { amount: $n(method.thresholdToActivate, "currency") }) }}
                  </template>
                </p>
              </template>
            </KikProperty>
            <template v-if="$flags.has('loyalty.program')">
              <KikProperty
                :name="$t('ShippingInformation3')"
                value="-20%"
                class="mt-2 justify-between border-b border-neutral-100 pb-2"
              />
              <KikProperty class="mt-2">
                <template #name>
                  <NuxtLink
                    class="underline"
                    :to="isAuthenticated ? $t('MyKikLink') : $t('MyKikContentLink')"
                  >
                    {{ $t('ShippingInformation4') }}
                  </NuxtLink>
                </template>
              </KikProperty>
            </template>
          </template>
        </KikDrawer>
      </div>

      <div class="mt-3 flex flex-wrap items-center gap-1">
        <p
          v-if="variant.isOnlineSale"
          class="text-sm mobile:text-xs"
        >
          {{ $t('Exclusive sale on online shop') }}
        </p>
        <p
          v-if="variant.price?.lowestTotalPrice && variant.isOnlineSale"
          class="text-sm mobile:text-xs"
        >
          |
        </p>
        <p
          v-if="variant.price?.lowestTotalPrice"
          class="text-sm mobile:text-xs"
        >
          {{ $t('30 Day Best Price') }}<sup>1</sup>: {{ $n(variant.price?.lowestTotalPrice.value, 'currency') }}
          <template v-if="variant.price?.lowestTotalPrice.percentage">
            ({{ variant.price?.lowestTotalPrice.percentage }}%)
          </template>
        </p>
      </div>
    </div>
    <div
      v-if="$flags.hasnt('shop.catalog.enabled')"
      class="mb-12 mt-4 mobile:mb-8"
    >
      <KikDrawer
        v-if="product.isMyKikDiscount"
        class="border-t-0"
        title="Customer cards"
      >
        <template #button>
          <div class="flex items-center gap-3 border-y py-3 text-sm mobile:text-xs">
            <NuxtImg
              src="/img/mykik.svg"
              alt="myKiK"
              title="myKiK"
              width="43"
              height="32"
              class="rotate-[-9deg] transform-gpu"
            />
            {{ $t('Price advantage for participants of the myKiK program') }}
            <SfIconInfo
              class="min-w-6"
              size="base"
            />
          </div>
        </template>
        <template #content>
          <i18n-t
            class="text-base"
            tag="div"
            keypath="Price advantage on articles marked with {0} in the current calendar month. Also applies to already reduced goods, but cannot be combined with other discounts."
          >
            <NuxtLink
              class="text-primary-700"
              :to="isAuthenticated ? $t('MyKikLink') : $t('MyKikContentLink')"
            >
              {{ $t('myKiK advantage card') }}
            </NuxtLink>
          </i18n-t>
        </template>
      </KikDrawer>
      <p
        v-if="variant.price.deposit"
        class="mt-2 text-sm mobile:text-xs"
      >
        {{ $t('+ {amount} deposit', { amount: $n(variant.price.deposit, "currency") }) }}
      </p>
      <template v-if="showEfficiency">
        <EfficiencySidebar
          :product="product"
          :attributes="attributes"
        />
        <NuxtLink
          :to="attributes.productdata_sheets"
          class="flex items-center gap-3 border-t py-3 text-sm mobile:justify-between mobile:text-xs"
          target="_blank"
        >
          {{ $t('Product data sheet') }}
          <SfIconInfo
            class="min-w-6"
            size="base"
          />
        </NuxtLink>
      </template>
      <div
        v-if="isSafetyTabShowed"
        class="flex cursor-pointer items-center gap-3 border-t py-3 text-sm mobile:justify-between mobile:text-xs"
        tabindex="0"
        @click="openSafetyInformation"
        @keydown.enter="openSafetyInformation"
        @keydown.space="openSafetyInformation"
      >
        {{ $t('Hazard notice') }}
        <SfIconInfo
          class="min-w-6"
          size="base"
        />
      </div>
    </div>
  </template>
</template>

<script lang="ts" setup>
import { SfIconInfo } from '@storefront-ui/vue';
import { useProduct } from '~/composables/custom/useProduct/useProduct';
import { useShippingProvider } from '~/composables/custom/useShippingProvider';
import EfficiencySidebar from '~/components/Product/EfficiencySidebar.vue';

interface Props {
  product: any;
  variantSku?: string;
  saleTag?: number | undefined;
  isMyKikDiscount?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variantSku: '',
  saleTag: undefined,
  isMyKikDiscount: false,
});

const emit = defineEmits(['open-safety-information']);
const { $i18n, $domain, $flags } = useNuxtApp();
const isAuthenticated = false;
const product = toRef(props, 'product');
const variantSku = toRef(props, 'variantSku');
const { productAvailabilityAndStock } = useProduct(product.value?.id);
const { shippingMethodsByCountry, loadShippingMethodByCountry, loadingByCountry } = useShippingProvider();

const variant = computed(() => {
  const selectedVariant = product.value?.variants?.find(({ sku }) => sku === variantSku.value);
  return {
    ...selectedVariant,
    price: productAvailabilityAndStock.value?.[variantSku.value]?.price ?? selectedVariant?.price,
  };
});

const finalPrice = computed(() => (variant.value.price?.deposit ? variant.value.price?.final - variant.value.price?.deposit : variant.value.price?.final));

const attributes = computed(() => variant.value?.attributes || {});

const openSafetyInformation = () => {
  emit('open-safety-information');
};

onMounted(async () => {
  if (!shippingMethodsByCountry?.value && !loadingByCountry.value) {
    await loadShippingMethodByCountry();
  }
});

/* eslint-disable camelcase */
const showEfficiency = computed(() => {
  const { energy_efficiency_class, energy_efficiency_label, productdata_sheets } = variant.value?.attributes || {};
  return energy_efficiency_class && productdata_sheets && energy_efficiency_label;
});

const productDescription = computed(() => {
  const { brandName = '' } = product.value;
  const { capacity_uom: capacityUnit = '', capacity_quantity: capacityQuantity = '', detailname = '' } = variant.value?.attributes || {};
  const capacityLabel = capacityUnit === 'Stk' ? $i18n.t(capacityUnit, { amount: capacityQuantity }) : null;

  const labelArray = [];

  if (capacityLabel) {
    labelArray.push(capacityLabel);
  }
  else if (capacityQuantity && capacityUnit) {
    labelArray.push(`${Intl.NumberFormat($domain.countryCode).format(capacityQuantity) ?? ''} ${$i18n.t(capacityUnit ?? '')}`);
  }
  if (brandName) {
    labelArray.push(brandName);
  }
  if (detailname) {
    labelArray.push(detailname);
  }

  return labelArray.join(', ');
});

const isSafetyTabShowed = computed(() => {
  const { danger_icons, signalword, hazard_notice } = variant.value?.attributes || {};
  return danger_icons?.length && signalword && hazard_notice;
});

const productName = computed(() => variant.value.attributes?.licence ? `${variant.value.attributes?.licence} ${product.value.name}` : product.value.name);
</script>
